import { FC, useCallback, useState } from "react";

import { useFindItemBySearchParamId } from "../../../../utils/hooks/use-find-item-by-search-param-id";
import { isGroupPage } from "../../../../utils/page-utils";

import { ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { AssessmentStudentQueueItem } from "components/materials/page/assessment/AssessmentStudentQueueItem";
import {
  getOfficialSubmission,
  getSubmissionsByAccessId,
  isSubmissionLate,
} from "components/materials/page/helpers";
import { ResponseType, useResponsesForPage } from "components/materials/page/responses";
import { t } from "i18n/i18n";
import { Listbox, type ListboxOption } from "mds/components/Listbox";
import { LoadingDotsIcon } from "mds/icons";
import { useAssessmentGradingFormContext } from "providers/AssessmentGradingFormProvider";
import {
  pageSelectors,
  sectionUserSelectors,
  selectCurrentSection,
  selectSortedSubmissionsByPageId,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { filterMap } from "utils/collections";
import { useQueryParamsActions } from "utils/routing/hooks/use-query-params";

type QueueDropdownOption = "all" | "assessed" | "unassessed" | "late";

type QueueListboxOption = ListboxOption & {
  id: QueueDropdownOption;
  disabled?: boolean;
};

const getQueueMenuDropdownOptions = (
  isGroup: boolean,
  allCount: number,
  assessedCount: number,
  unassessedCount: number,
  lateCount: number,
): QueueListboxOption[] => {
  const principalType = isGroup ? "group" : "student";

  return [
    {
      id: "all",
      title: t(`student_assessment_queue.${principalType}.all`, { num: allCount }),
      disabled: allCount === 0,
    },
    {
      id: "assessed",
      title: t(`student_assessment_queue.${principalType}.assessed`, {
        num: assessedCount,
      }),
      disabled: assessedCount === 0,
    },
    {
      id: "unassessed",
      title: t(`student_assessment_queue.${principalType}.unassessed`, {
        num: unassessedCount,
      }),
      disabled: unassessedCount === 0,
    },
    {
      id: "late",
      title: t("student_assessment_queue.late", { num: lateCount }),
      disabled: lateCount === 0,
    },
  ];
};

interface AssessmentStudentQueueDropdownMenuProps {
  selectedOption: QueueDropdownOption;
  onOptionChange: (option: QueueDropdownOption) => void;
  isGroup: boolean;
  allCount: number;
  assessedCount: number;
  unassessedCount: number;
  lateCount: number;
}
const AssessmentStudentQueueDropdownMenu: FC<AssessmentStudentQueueDropdownMenuProps> = ({
  selectedOption,
  onOptionChange,
  isGroup,
  allCount,
  assessedCount,
  unassessedCount,
  lateCount,
}) => {
  const options = getQueueMenuDropdownOptions(
    isGroup,
    allCount,
    assessedCount,
    unassessedCount,
    lateCount,
  );

  return (
    <div className="mb-4 flex">
      <Listbox
        buttonSize="s"
        className="w-[206px]"
        eventPropertyKey="fieldValue"
        items={options}
        selectedValue={selectedOption}
        onSelectionChange={(item) => onOptionChange(item.id)}
      />
    </div>
  );
};

interface AssessmentStudentQueueProps {
  pageId: string;
}

export const AssessmentStudentQueue: FC<AssessmentStudentQueueProps> = ({ pageId }) => {
  const { isAiAssessmentLoading, saveConfirmation } = useAssessmentGradingFormContext();
  const currentSection = useObjectSelector(selectCurrentSection, ["anonymous_grading_enabled"]);
  const { setQueryParams } = useQueryParamsActions();
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["id", "due_at", "assessments_published_at", "interaction_mode"],
  );
  const sectionUsers = useListSelector(sectionUserSelectors.filterForCurrentSection, [
    "id",
    "user_id",
  ]);
  const submissions = useListSelector(
    (s) => selectSortedSubmissionsByPageId(s, pageId),
    ["created_at", "section_user_id", "page_group_id", "official_at"],
  );
  const { responses } = useResponsesForPage(pageId);

  const [filterBy, setFilterBy] = useState<QueueDropdownOption>("all");

  const assessmentsPublished = !!page?.assessments_published_at;
  const isGroup = !!page && isGroupPage(page);

  const anonymize = !assessmentsPublished && currentSection?.anonymous_grading_enabled;
  const selectedResponse = useFindItemBySearchParamId(responses, ACCESS_ID_QUERY_PARAM, true);

  const assessedResponseById = Object.fromEntries(
    filterMap(
      responses,
      (response) => {
        const studentSubmissions = getSubmissionsByAccessId(response.id, sectionUsers, submissions);
        return Boolean(getOfficialSubmission(studentSubmissions));
      },
      (response) => [response.id, response] as const,
    ),
  );
  const assessedResponses = Object.values(assessedResponseById);

  const unassessedResponses = responses.filter((response) => !assessedResponseById[response.id]);

  const lateResponses = responses.filter((response) => {
    const studentSubmissions = getSubmissionsByAccessId(response.id, sectionUsers, submissions);
    return studentSubmissions.some((submission) =>
      page?.due_at ? isSubmissionLate(submission, page?.due_at) : false,
    );
  });

  const filteredResponses =
    filterBy === "late"
      ? lateResponses
      : filterBy === "assessed"
        ? assessedResponses
        : filterBy === "unassessed"
          ? unassessedResponses
          : responses;

  const handleResponseClick = useCallback(
    (response: ResponseType) => {
      setQueryParams(
        {
          [ACCESS_ID_QUERY_PARAM]: response.id,
          [SUBMISSION_ID_QUERY_PARAM]: null,
        },
        { replace: false },
      );
    },
    [setQueryParams],
  );

  return (
    <div className="relative">
      {isAiAssessmentLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <LoadingDotsIcon />
        </div>
      )}

      <div
        className="ml-auto h-full w-[240px] overflow-y-auto rounded-2xl border border-black-tint-90 bg-white p-4"
        role="list"
      >
        <AssessmentStudentQueueDropdownMenu
          allCount={responses.length}
          assessedCount={assessedResponses.length}
          isGroup={isGroup}
          lateCount={lateResponses.length}
          selectedOption={filterBy}
          unassessedCount={unassessedResponses.length}
          onOptionChange={setFilterBy}
        />
        {filteredResponses.map((response) => (
          <AssessmentStudentQueueItem
            anonymize={!!anonymize}
            isAssessedResponse={!!assessedResponseById[response.id]}
            isSaved={saveConfirmation?.accessId === response.id}
            isSelected={selectedResponse?.id === response.id}
            key={response.id}
            pageId={pageId}
            response={response}
            onResponseClick={handleResponseClick}
          />
        ))}
      </div>
    </div>
  );
};
